import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import { addLevel, addSublevel, getLevelTreeStructure, updateLevel, updateLevelTreeStructure } from "api/level";
import { LevelStructureType, LevelType, LevelDefault } from "types/dict";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  OnConnect,
  OnEdgesChange,
  OnNodesChange,
  ReactFlow,
  Handle,
  Position,
  Connection,
  useReactFlow,
  ReactFlowProvider,
  Panel,
  Controls,
  MiniMap,
  Background,
  BaseEdge,
  EdgeLabelRenderer,
  getStraightPath,
  EdgeProps,
} from "@xyflow/react";

function Flow() {
  const { levels, getLevelsData, getLevelRootsData, notify } = useSharedState();
  const { levelId } = useParams();
  const navigate = useNavigate();
  const reactFlowInstance = useReactFlow();

  const [levelItem, setLevelItem] = useState<LevelStructureType>();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [isFlowReady, setFlowReady] = useState<boolean>(false);
  const [nextId, setNextId] = useState<number>(1);

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const CustomEdge = ({ id, sourceX, sourceY, targetX, targetY, source, target }: EdgeProps) => {
    const { notify } = useSharedState();
    const { levelId } = useParams();
    const { setEdges } = useReactFlow();

    const [edgePath, labelX, labelY] = getStraightPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
    });

    const handleDelete = async () => {
      if (levelId) {
        // Existing level - use API to remove edge
        const payload = {
          add_edges: [],
          remove_edges: [
            {
              parent_id: Number(source),
              child_id: Number(target),
            },
          ],
        };

        try {
          setFormLoading(true);
          await updateLevelTreeStructure(Number(levelId), payload);
          notify("Связь удалена", "success");
          getLevelTreeStructureData();
        } catch (error: any) {
          let err = JSON.stringify(error?.response?.data?.detail);
          notify(err, "danger", "Ошибка при удалении связи");
        } finally {
          setFormLoading(false);
        }
      } else {
        // New level - just remove from edges array
        setEdges((edges) => edges.filter((edge) => edge.id !== id));
      }
    };

    return (
      <>
        <BaseEdge id={id} path={edgePath} />
        <EdgeLabelRenderer>
          <button
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "all",
            }}
            className="nodrag nopan btn btn-danger btn-sm"
            onClick={handleDelete}
          >
            Удалить связь
          </button>
        </EdgeLabelRenderer>
      </>
    );
  };

  const CustomNode = ({ data, id, isConnectable }: any) => {
    const { notify } = useSharedState();
    const { levelId } = useParams();
    const { setNodes, getNode } = useReactFlow();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      console.log(name, value, Number(value));

      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                [name]: name === "budget_type" ? value : Number(value) ? Number(value) : null,
              },
            };
          }
          return node;
        })
      );
    };

    const handleDelete = () => {
      if (data.budget_type_id) {
        // Update existing level status
        updateLevel({ ...data, status: false })
          .then(() => {
            notify("Уровень удален", "success");
          })
          .catch((error) => {
            let err = JSON.stringify(error?.response?.data?.detail);
            notify(err, "danger", "Ошибка при удалении уровня");
          });
      }
      setNodes((nodes) => nodes.filter((n) => n.id !== id));
    };

    const handleSave = async () => {
      // Add new level
      try {
        await addSublevel(data, Number(levelId));
        notify("Уровень добавлен", "success");
        getLevelTreeStructureData();
      } catch (error: any) {
        let err = JSON.stringify(error?.response?.data?.detail);
        notify(err, "danger", "Ошибка при добавлении уровня");
      }

      setNodes((nodes) => nodes.filter((n) => n.id !== id));
    };

    return (
      <div className="card" style={{ minWidth: "200px" }}>
        <div className="card-body">
          <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
          <div className="mb-2">
            <input
              type="text"
              className="form-control"
              placeholder="Название уровня"
              name="budget_type"
              value={data.budget_type || ""}
              onChange={handleInputChange}
              disabled={data.budget_type_id !== null && data.budget_type_id !== undefined}
            />
          </div>
          <div className="mb-2">
            <input
              type="number"
              className="form-control"
              placeholder={data.level_number === 0 ? "Мин. уровень прогноза" : "Номер уровня"}
              name={data.level_number === 0 ? "forecast_min_level" : "level_number"}
              value={data.level_number === 0 ? data.forecast_min_level || "" : data.level_number || ""}
              onChange={handleInputChange}
              disabled={data.budget_type_id !== null && data.budget_type_id !== undefined}
              min={data.level_number === 0 ? 1 : 1}
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            {data.level_number !== 0 && (
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                Удалить
              </button>
            )}
            {data.level_number !== 0 && data.status === null && (
              <button className="btn btn-primary btn-sm" onClick={handleSave}>
                Сохранить
              </button>
            )}
          </div>
          <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
        </div>
      </div>
    );
  };

  const onNodesChange: OnNodesChange = (changes: NodeChange<Node>[]) => {
    const isDragEnded = changes.every((node) => (node as any).dragging === false);
    if (isDragEnded && levelId) {
      const changedNodes = nodes.filter((n) => changes.some((change) => (change as any).id === n.id));

      if (changedNodes.some((n) => n.data.status === null)) {
        notify("Сначала сохраните уровень", "danger");
        return;
      }

      changedNodes.forEach((node) => {
        updateLevelData(node);
      });
    }

    setNodes((nodesSnapshot) => applyNodeChanges(changes, nodesSnapshot!));
  };

  const onConnect: OnConnect = async (params: Connection) => {
    const sourceNode = reactFlowInstance.getNode(params.source);
    const targetNode = reactFlowInstance.getNode(params.target);

    // Check if source level number is less than target level number
    const sourceLevelNumber = sourceNode?.data.level_number;
    const targetLevelNumber = targetNode?.data.level_number;

    if (sourceLevelNumber === targetLevelNumber) {
      notify("Нельзя соединять уровни одного номера", "danger");
      return;
    }

    if (Number(sourceLevelNumber) > Number(targetLevelNumber)) {
      notify("Родительский уровень должен быть выше дочернего уровня", "danger");
      return;
    }

    if (params.source === params.target) {
      notify("Нельзя соединять уровень с самим собой", "danger");
      return;
    }

    if (sourceLevelNumber === null || targetLevelNumber === null) {
      notify("Нельзя соединять уровни без номера", "danger");
      return;
    }

    if (targetNode?.data.status === null) {
      notify("Сначала сохраните уровень", "danger");
      return;
    }

    levelId && (await createEdge(Number(sourceNode?.id), Number(targetNode?.id)));

    // Update target node's direct_parent_id
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === params.target) {
          return {
            ...node,
            data: {
              ...node.data,
              direct_parent_id: Number(params.source),
            },
          };
        }
        return node;
      })
    );

    setEdges((eds) => addEdge(params, eds));
  };

  const addNewNode = () => {
    const newNode: Node = {
      id: String(nextId),
      type: "custom",
      position: { x: 100, y: 100 },
      data: {
        ...LevelDefault,
        budget_type: "",
        level_number: null,
      },
    };

    setNodes((nds) => [...nds, newNode]);
    setNextId((id) => id + 1);
  };

  useEffect(() => {
    if (levelId) {
      getLevelTreeStructureData();
    } else {
      // Initialize with a main level node for new hierarchy
      const mainNode: Node = {
        id: "0",
        type: "custom",
        position: { x: 0, y: 0 },
        data: {
          ...LevelDefault,
          budget_type: "",
          level_number: 0,
        },
      };
      setNodes([mainNode]);
      setNextId(1);
      setFlowReady(true);
    }
  }, [levelId]);

  const openLevelModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const getLevelTreeStructureData = async () => {
    try {
      const res: LevelStructureType = await getLevelTreeStructure(Number(levelId));
      setLevelItem(res);

      // Transform nodes to ReactFlow format
      const flowNodes: Node[] = res.nodes.map((node, index) => ({
        id: String(node.budget_type_id || 0),
        position: node.position ? node.position : { x: index * 250, y: (node.level_number || 0) * 150 },
        data: {
          ...node,
          level_number: node.level_number || 0,
          budget_type: node.budget_type || "",
        },
        type: "custom",
      }));

      // Transform edges to ReactFlow format
      const flowEdges: Edge[] = res.edges.map((edge) => ({
        id: `${edge.child_id}-${edge.parent_id}`,
        source: String(edge.parent_id),
        target: String(edge.child_id),
        type: "custom",
      }));

      // Find max id to set nextId
      const maxId = Math.max(...res.nodes.map((node) => node.budget_type_id || 0)) + 1;
      setNextId(maxId);

      setNodes(flowNodes);
      setEdges(flowEdges);
      setFlowReady(true);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при загрузке структуры уровня");
    }
  };

  const submitLevel = async () => {
    // if (!validateForm()) {
    //   notify("Заполните все поля!", "danger");
    //   return;
    // }
    setFormLoading(true);

    const parentNode = nodes.find((n) => n.data.level_number === 0);
    const childrenNodes = nodes.filter((n) => n.data.level_number !== 0);

    const childrenPayload: LevelType[] = childrenNodes.map((c) => ({
      budget_type: String(c.data.budget_type) || null,
      budget_type_id: Number(c.id) || null,
      direct_parent_id: Number(c.data.direct_parent_id) || null,
      forecast_min_level: Number(c.data.forecast_min_level) || null,
      level_number: Number(c.data.level_number),
      max_levels: Number(c.data.max_levels) || null,
      parent_budget_type_id: Number(c.data.parent_budget_type_id) || null,
      position: c.position ? { x: Math.round(c.position.x), y: Math.round(c.position.y) } : null,
      status: Boolean(c.data.status) || null,
    }));

    try {
      if (parentNode) {
        await addLevel(
          String(parentNode.data.budget_type),
          Math.max(...childrenNodes.map((c) => (c.data.level_number as number) || 0)),
          Number(parentNode.data.forecast_min_level),
          childrenPayload
        );
        notify("Уровень создан", "success");
      }
      await getLevelsData();

      // Find created levels in the shared state
      const findLevelInState = (budgetType: string, levelNumber: number) => {
        return levels.find((level) => level.budget_type === budgetType && level.level_number === levelNumber);
      };

      // Find parent level
      const createdParentLevel = parentNode ? findLevelInState(String(parentNode.data.budget_type), 0) : null;

      // Create edges for children with direct_parent_id
      if (createdParentLevel) {
        for (const child of childrenNodes) {
          if (child.data.direct_parent_id) {
            const createdChildLevel = findLevelInState(String(child.data.budget_type), Number(child.data.level_number));

            if (createdChildLevel && createdChildLevel.budget_type_id) {
              await createEdge(createdParentLevel.budget_type_id!, createdChildLevel.budget_type_id);
            }
          }
        }
      }

      setFormSubmitted(true);
      getLevelRootsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", levelId ? "Ошибка при обновлении уровня" : "Ошибка при создании уровня");
    } finally {
      setFormLoading(false);
    }
  };

  const updateLevelData = async (node: Node) => {
    const level: LevelType = {
      budget_type: String(node.data?.budget_type) || null,
      budget_type_id: Number(node.id) || null,
      direct_parent_id: Number(node.data?.direct_parent_id) || null,
      forecast_min_level: Number(node.data?.forecast_min_level) || null,
      level_number: Number(node.data?.level_number),
      max_levels: Number(node.data?.max_levels) || null,
      parent_budget_type_id: Number(node.data?.parent_budget_type_id) || null,
      position: node.position ? { x: Math.round(node.position.x), y: Math.round(node.position.y) } : null,
      status: Boolean(node.data?.status) || null,
    };

    try {
      setFormLoading(true);
      await updateLevel(level);

      notify("Изменения сохранены", "success");
      getLevelTreeStructureData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении изменений");
    } finally {
      setFormLoading(false);
    }
  };

  const createEdge = async (parentId: number, childId: number) => {
    const payload = {
      add_edges: [
        {
          parent_id: parentId,
          child_id: childId,
        },
      ],
      remove_edges: [],
    };

    try {
      setFormLoading(true);
      await updateLevelTreeStructure(Number(levelId), payload);

      notify("Изменения сохранены", "success");
      getLevelTreeStructureData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении изменений");
    } finally {
      setFormLoading(false);
    }
  };

  const hasChanges = false; // changedLevels.size > 0 || rows.some((row) => !row.budget_type_id && row.budget_type);

  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);
  const edgeTypes = useMemo(() => ({ custom: CustomEdge }), []);

  if (!isFlowReady) return null;

  const levelBody = (
    <div style={{ margin: -24 }}>
      <div style={{ width: "100%", height: "700px" }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{ type: "custom" }}
          fitView
        >
          <Panel position="top-right">
            <button type="button" className="btn btn-outline-primary" onClick={addNewNode}>
              Добавить уровень
            </button>
          </Panel>

          <Controls />
          <Background color="skyblue" />
          <MiniMap pannable zoomable zoomStep={1} bgColor="#80A1B4" />
        </ReactFlow>
      </div>
    </div>
  );

  const levelFooter = !levelId ? (
    <button type="button" className="btn btn-primary d-flex gap-1" onClick={submitLevel} disabled={isFormLoading}>
      Создать уровень
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  ) : (
    <button
      type="button"
      className="btn btn-primary d-flex gap-1"
      // onClick={handleSaveChanges}
      disabled={isFormLoading || !hasChanges}
    >
      Сохранить
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <ModalComponent
      header={levelId ? "Структура уровня" : "Новая структура уровней"}
      body={levelBody}
      footer={levelFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}

export default function LevelStructure() {
  return (
    <ReactFlowProvider>
      <Flow />
    </ReactFlowProvider>
  );
}
